/**
 * Lightweight SFX synth — small dictionary of pre-described tones rendered
 * straight to a Web Audio context. No external assets, no jsfxr dep.
 *
 * Master gain follows two factors: a `volume` multiplier (set by the audio
 * settings dialog, 0..1) and a `muted` boolean. Real output gain = mute ? 0
 * : (BASE_GAIN * volume).
 */

type Voice = {
  type: OscillatorType;
  freqStart: number;
  freqEnd: number;
  durSec: number;
  gain: number;
  noiseMix?: number; // 0..1
};

const SFX_LIB: Record<string, Voice> = {
  build: { type: 'square', freqStart: 220, freqEnd: 440, durSec: 0.25, gain: 0.18 },
  train: { type: 'triangle', freqStart: 320, freqEnd: 200, durSec: 0.18, gain: 0.18 },
  hit: { type: 'sawtooth', freqStart: 180, freqEnd: 80, durSec: 0.12, gain: 0.18, noiseMix: 0.5 },
  death: { type: 'sawtooth', freqStart: 240, freqEnd: 60, durSec: 0.45, gain: 0.22, noiseMix: 0.3 },
  attack: { type: 'square', freqStart: 600, freqEnd: 380, durSec: 0.08, gain: 0.12 },
  victory: { type: 'square', freqStart: 440, freqEnd: 880, durSec: 0.7, gain: 0.18 },
  defeat: { type: 'sawtooth', freqStart: 260, freqEnd: 60, durSec: 0.7, gain: 0.18 },
  // UI / selection feedback
  'ui-click': { type: 'square', freqStart: 1200, freqEnd: 1400, durSec: 0.04, gain: 0.06 },
  building: {
    type: 'sawtooth',
    freqStart: 320,
    freqEnd: 240,
    durSec: 0.32,
    gain: 0.16,
    noiseMix: 0.6,
  }, // hammer-ish thud
  bell: { type: 'triangle', freqStart: 880, freqEnd: 660, durSec: 0.7, gain: 0.16 }, // chime
  // Mine production tick — short pickaxe-on-stone "tink".
  mineWork: { type: 'square', freqStart: 720, freqEnd: 380, durSec: 0.08, gain: 0.1, noiseMix: 0.5 },
  // Huta smelting — low rumble + clank.
  smelt: { type: 'sawtooth', freqStart: 140, freqEnd: 90, durSec: 0.5, gain: 0.14, noiseMix: 0.4 },
  // Fishing boat splash — wet noise burst.
  splash: { type: 'sine', freqStart: 380, freqEnd: 220, durSec: 0.18, gain: 0.1, noiseMix: 0.8 },
  // Cavalry charge — galloping low thud.
  gallop: { type: 'square', freqStart: 180, freqEnd: 120, durSec: 0.18, gain: 0.14, noiseMix: 0.4 },
  // Catapult fire — heavy thwock.
  catapult: { type: 'sawtooth', freqStart: 260, freqEnd: 60, durSec: 0.35, gain: 0.22, noiseMix: 0.6 },
  // Worker enter/exit a building — thump like a wooden door.
  doorThump: { type: 'triangle', freqStart: 240, freqEnd: 140, durSec: 0.14, gain: 0.12, noiseMix: 0.3 },
};

const BASE_GAIN = 0.6;

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  muted = true;
  /** Mutes all gameplay SFX (building hammers, attacks, deaths, ...) but
   *  leaves UI clicks and background music untouched. Set while the in-game
   *  menu is open. */
  simPaused = false;
  private volume = 1;
  private lastPlay: Record<string, number> = {};

  setMuted(m: boolean): void {
    this.muted = m;
    this.applyGain();
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    this.applyGain();
  }

  private applyGain(): void {
    if (!this.master) return;
    this.master.gain.value = this.muted ? 0 : BASE_GAIN * this.volume;
  }

  private ensure(): void {
    if (this.ctx) return;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.applyGain();
    this.master.connect(this.ctx.destination);
  }

  play(name: keyof typeof SFX_LIB): void {
    this.ensure();
    if (!this.ctx || !this.master) return;
    if (this.muted || this.volume <= 0) return;
    // Sim-pause: silence all gameplay SFX; UI clicks still go through so the
    // menu's own buttons sound responsive.
    if (this.simPaused && name !== 'ui-click') return;

    // Throttle: drop duplicates within 50 ms.
    const now = this.ctx.currentTime;
    if (this.lastPlay[name] && now - this.lastPlay[name] < 0.05) return;
    this.lastPlay[name] = now;

    const v = SFX_LIB[name];
    if (!v) return;

    const osc = this.ctx.createOscillator();
    osc.type = v.type;
    osc.frequency.setValueAtTime(v.freqStart, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, v.freqEnd), now + v.durSec);

    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(v.gain, now + 0.005);
    env.gain.exponentialRampToValueAtTime(0.0001, now + v.durSec);

    osc.connect(env).connect(this.master);
    osc.start(now);
    osc.stop(now + v.durSec + 0.02);

    if (v.noiseMix) {
      const buf = this.makeNoise(v.durSec);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const ng = this.ctx.createGain();
      ng.gain.value = v.gain * v.noiseMix;
      src.connect(ng).connect(this.master);
      src.start(now);
      src.stop(now + v.durSec + 0.02);
    }
  }

  resume(): void {
    this.ensure();
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume();
  }

  private makeNoise(durSec: number): AudioBuffer {
    const ctx = this.ctx!;
    const len = Math.floor(ctx.sampleRate * durSec);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
    return buf;
  }
}
